import * as React from 'react';

import { IStock } from '@notino/shared/definitions/custom-definitions';
import { CatalogVariantFragmentFragment } from '@notino/shared/definitions/types';

import PriceLabel from '@components/PriceLabel';

import { useIsDualPrice } from '../../hooks/useIsDualPrice';
import { useShouldRenderEligibleAction } from '../utils/useShouldRenderEligibleAction';
import { OriginalPrice } from '../VariantsInSelectBox/TagDiscount/OriginalPrice';

import {
  DualPrice,
  DualPriceSplitter,
  DualPriceWrapper,
  NoVoucherCurrencyWrapper,
  NoVoucherPriceWrapper,
  PriceBelowMinConditionCurrencyWrapper,
  PriceBelowMinConditionWrapper,
  SmallerCurrencyWrapper,
  SmallerPriceWrapper,
} from './styled';
import { usePriceLabelState } from './usePriceLabelState';

interface PriceDisplayProps {
  variant: CatalogVariantFragmentFragment;
}

export const PriceDisplay: React.FC<PriceDisplayProps> = ({ variant }) => {
  const {
    isDualPrice,
    getLeftSideFormattedPrice,
    getRightSideFormattedPrice,
    leftSideCurrency,
    rightSideCurrency,
  } = useIsDualPrice();
  const shouldRenderAction = useShouldRenderEligibleAction();

  const priceState = usePriceLabelState();

  const inStock = variant.stockAvailability.code !== IStock.outOfStock;
  const isSmall = priceState === 'default';

  const getPriceWrapper = () => {
    if (priceState === 'default') {
      return SmallerPriceWrapper;
    }

    if (priceState === 'noVoucherDiscount') {
      return NoVoucherPriceWrapper;
    }

    return PriceBelowMinConditionWrapper;
  };
  const getCurrencyWrapper = () => {
    if (priceState === 'default') {
      return SmallerCurrencyWrapper;
    }

    if (priceState === 'noVoucherDiscount') {
      return NoVoucherCurrencyWrapper;
    }

    return PriceBelowMinConditionCurrencyWrapper;
  };

  if (!inStock || shouldRenderAction) {
    return null;
  }

  if (isDualPrice) {
    return shouldRenderAction ? (
      <OriginalPrice variant={variant} />
    ) : (
      <DualPriceWrapper id="pd-price" data-testid="pd-dual-price">
        <DualPrice isSmall={isSmall}>
          {getLeftSideFormattedPrice(variant.price.value)}{' '}
          <span>{leftSideCurrency}</span>
        </DualPrice>
        <DualPriceSplitter isSmall={isSmall}>/</DualPriceSplitter>
        <DualPrice isSmall={isSmall}>
          {getRightSideFormattedPrice(variant.price.value)}{' '}
          <span>{rightSideCurrency}</span>
        </DualPrice>
      </DualPriceWrapper>
    );
  }

  return shouldRenderAction ? (
    <OriginalPrice variant={variant} />
  ) : (
    <PriceLabel
      price={variant.price}
      wrapperComponent={getPriceWrapper()}
      currencyComponent={getCurrencyWrapper()}
      id="pd-price"
      dataTestId="pd-price"
    />
  );
};
